import React from "react";
import { X } from "lucide-react";
import NodeForm from "../nodes/NodeForm";
import NodeTransitionsSection from "../nodes/NodeTransitionsSection";

interface NodeEditorSidebarProps {
  open: boolean;
  onClose: () => void;
  node?: any;
  isCreating?: boolean;
  scripts: any[];
  nodes: any[];
  transitions?: any[];
  onTransitionsChange?: (next: any[]) => void;
  onSubmit: (data: any) => Promise<void> | void;
  onDelete?: () => Promise<void> | void;
  isSubmitting?: boolean;
  onChange?: (data: any, dirty: boolean) => void;
  showScriptSelect?: boolean;
  testingConfig?: { conversation_key?: string; session_id?: string };
  // New: whether the sidebar is shown inside Flow edit page
  isFlowPage?: boolean;
  // New: script id to use for creating local (script-level) transitions
  scriptIdForContext?: number;
  // New: read-only mode disables editing controls
  readOnly?: boolean;
}

export default function NodeEditorSidebar({
  open,
  onClose,
  node,
  isCreating = false,
  scripts,
  nodes,
  transitions = [],
  onTransitionsChange,
  onSubmit,
  onDelete,
  isSubmitting = false,
  onChange,
  showScriptSelect = false,
  testingConfig,
  isFlowPage = false,
  scriptIdForContext,
  readOnly = false,
}: NodeEditorSidebarProps) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-xl bg-white shadow-xl flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">
            {isCreating ? "Create Node" : "Edit Node"}
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {readOnly ? (
            <div className="mb-3 p-3 rounded border border-yellow-200 bg-yellow-50 text-yellow-800 text-sm">
              Only draft flows are allowed to be edited.
            </div>
          ) : null}
          <div className="relative">
            {readOnly ? (
              <div className="absolute inset-0 bg-gray-100/30 rounded z-10" />
            ) : null}
            <div className={readOnly ? "pointer-events-none" : undefined}>
              <NodeForm
                node={isCreating ? undefined : node}
                onSubmit={onSubmit}
                onCancel={onClose}
                isSubmitting={isSubmitting}
                scripts={scripts}
                nodes={nodes}
                onChange={onChange}
                testingConfig={testingConfig}
                extraSectionBeforeActions={
                  !isCreating && transitions && onTransitionsChange
                    ? (() => {
                        const rawGroupId =
                          typeof node?.node_group_id === "number" &&
                          Number.isFinite(node?.node_group_id) &&
                          node?.node_group_id > 0
                            ? node!.node_group_id
                            : null;
                        const rawScriptId =
                          typeof node?.script_id === "number" &&
                          Number.isFinite(node?.script_id) &&
                          node?.script_id > 0
                            ? node!.script_id
                            : Number.isFinite(scriptIdForContext as any)
                              ? (scriptIdForContext as number)
                              : null;
                        const isGlobalNode = !!rawGroupId;
                        // In Flow page, if node is global, show Local Transitions (script-level) only
                        if (isFlowPage && isGlobalNode) {
                          return (
                            <NodeTransitionsSection
                              title="Local Transitions"
                              groupId={null}
                              scriptId={rawScriptId}
                              currentNodeId={node?.id}
                              nodes={nodes}
                              transitions={transitions}
                              onChange={onTransitionsChange}
                              readOnly={readOnly}
                            />
                          );
                        }
                        // Default behavior: show group or script transitions based on node ownership
                        const groupIdToUse = rawGroupId ?? null;
                        const scriptIdToUse = groupIdToUse
                          ? null
                          : (rawScriptId ?? null);
                        return (
                          <NodeTransitionsSection
                            groupId={groupIdToUse}
                            scriptId={scriptIdToUse}
                            currentNodeId={node?.id}
                            nodes={nodes}
                            transitions={transitions}
                            onChange={onTransitionsChange}
                            readOnly={readOnly}
                          />
                        );
                      })()
                    : undefined
                }
                showScriptSelect={showScriptSelect}
                onDelete={!isCreating ? onDelete : undefined}
                onlyShowExtraSection={(() => {
                  const rawGroupId =
                    typeof node?.node_group_id === "number" &&
                    Number.isFinite(node?.node_group_id) &&
                    node?.node_group_id > 0
                      ? node!.node_group_id
                      : null;
                  const isGlobalNode = !!rawGroupId;
                  return Boolean(isFlowPage && isGlobalNode);
                })()}
                // Pass read-only mode down so NodeForm can disable editing
                readOnly={readOnly}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
